<template>
  <div class="trend-chart">
    <div class="trend-legend">
      <span v-for="s in SERIES" :key="s.key" class="trend-legend-item">
        <span class="trend-legend-dot" :style="{ background: s.color }"></span>{{ s.name }}
      </span>
    </div>

    <div v-if="data.length === 0" class="trend-empty">暂无数据</div>

    <div v-else class="trend-svg-wrap" @mouseleave="hoverIndex = null">
      <svg
        ref="svgRef"
        :viewBox="`0 0 ${W} ${H}`"
        class="trend-svg"
        @mousemove="onMouseMove"
      >
        <!-- 网格与 y 轴刻度 -->
        <g v-for="t in yTicks" :key="t">
          <line :x1="PAD.l" :y1="y(t)" :x2="W - PAD.r" :y2="y(t)" class="trend-grid" />
          <text :x="PAD.l - 8" :y="y(t) + 4" class="trend-ylabel" text-anchor="end">{{ t }}</text>
        </g>

        <!-- x 轴标签 -->
        <text
          v-for="(p, i) in data"
          v-show="i % xStride === 0"
          :key="p.date"
          :x="x(i)"
          :y="H - 8"
          class="trend-xlabel"
          text-anchor="middle"
        >
          {{ p.date.slice(5) }}
        </text>

        <!-- 悬停竖线 -->
        <line
          v-if="hoverIndex != null"
          :x1="x(hoverIndex)"
          :y1="PAD.t"
          :x2="x(hoverIndex)"
          :y2="H - PAD.b"
          class="trend-hover-line"
        />

        <!-- 折线 -->
        <path v-for="s in SERIES" :key="s.key" :d="paths[s.key]" fill="none" :stroke="s.color" stroke-width="2" />

        <!-- 数据点 -->
        <g v-if="hoverIndex != null">
          <circle
            v-for="s in SERIES"
            :key="s.key"
            :cx="x(hoverIndex)"
            :cy="y(Number(data[hoverIndex][s.key]) || 0)"
            r="4"
            fill="#fff"
            :stroke="s.color"
            stroke-width="2"
          />
        </g>
      </svg>

      <!-- Tooltip -->
      <div
        v-if="hoverIndex != null && data[hoverIndex]"
        class="trend-tooltip"
        :style="{ left: (x(hoverIndex) / W) * 100 + '%' }"
      >
        <div class="trend-tooltip-date">{{ data[hoverIndex].date }}</div>
        <div v-for="s in SERIES" :key="s.key" class="trend-tooltip-row">
          <span class="trend-legend-dot" :style="{ background: s.color }"></span>
          {{ s.name }}：<b>{{ Number(data[hoverIndex][s.key]) || 0 }}</b>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const W = 800
const H = 320
const PAD = { l: 48, r: 16, t: 16, b: 28 }

const SERIES = [
  { key: 'sessions', name: '会话数', color: '#5B8FF9' },
  { key: 'messages', name: '消息数', color: '#5AD8A6' },
  { key: 'aiCalls', name: 'AI 调用', color: '#F6BD16' }
]

export default {
  name: 'TrendChart',
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      W,
      H,
      PAD,
      SERIES,
      hoverIndex: null
    }
  },
  computed: {
    maxValue () {
      let m = 0
      for (const p of this.data) {
        for (const s of SERIES) m = Math.max(m, Number(p[s.key]) || 0)
      }
      return m
    },
    /** 将最大值向上取整到 1/2/5 × 10^n */
    yMax () {
      const m = this.maxValue
      if (m <= 0) return 1
      const pow = Math.pow(10, Math.floor(Math.log10(m)))
      for (const mult of [1, 2, 5, 10]) {
        if (mult * pow >= m) return mult * pow
      }
      return m
    },
    yTicks () {
      const ticks = []
      const divisions = 5
      for (let i = 0; i <= divisions; i++) {
        ticks.push(Math.round((this.yMax / divisions) * i))
      }
      return ticks
    },
    xStride () {
      return Math.max(1, Math.ceil(this.data.length / 8))
    },
    innerW () {
      return W - PAD.l - PAD.r
    },
    innerH () {
      return H - PAD.t - PAD.b
    },
    paths () {
      const out = {}
      for (const s of SERIES) {
        out[s.key] = this.smoothPath(this.data.map((p, i) => ({
          x: this.x(i),
          y: this.y(Number(p[s.key]) || 0)
        })))
      }
      return out
    }
  },
  methods: {
    x (i) {
      const n = this.data.length
      if (n <= 1) return PAD.l + this.innerW / 2
      return PAD.l + (i / (n - 1)) * this.innerW
    },
    y (v) {
      return H - PAD.b - (v / this.yMax) * this.innerH
    },
    smoothPath (points) {
      if (points.length === 0) return ''
      if (points.length === 1) return `M ${points[0].x} ${points[0].y}`
      let d = `M ${points[0].x} ${points[0].y}`
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[Math.max(0, i - 1)]
        const p1 = points[i]
        const p2 = points[i + 1]
        const p3 = points[Math.min(points.length - 1, i + 2)]
        const c1x = p1.x + (p2.x - p0.x) / 6
        const c1y = p1.y + (p2.y - p0.y) / 6
        const c2x = p2.x - (p3.x - p1.x) / 6
        const c2y = p2.y - (p3.y - p1.y) / 6
        d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`
      }
      return d
    },
    onMouseMove (e) {
      const svg = this.$refs.svgRef
      if (!svg || this.data.length === 0) return
      const rect = svg.getBoundingClientRect()
      const viewBoxX = ((e.clientX - rect.left) / rect.width) * W
      const n = this.data.length
      const ratio = (viewBoxX - PAD.l) / this.innerW
      const idx = Math.round(ratio * (n - 1))
      this.hoverIndex = Math.min(n - 1, Math.max(0, idx))
    }
  }
}
</script>

<style scoped>
.trend-chart {
  width: 100%;
}

.trend-legend {
  display: flex;
  gap: 16px;
  padding: 0 0 8px 8px;
  font-size: 12px;
  color: #909399;
}

.trend-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.trend-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.trend-empty {
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 13px;
}

.trend-svg-wrap {
  position: relative;
  width: 100%;
}

.trend-svg {
  width: 100%;
  height: auto;
  display: block;
}

.trend-grid {
  stroke: #ebeef5;
  stroke-width: 1;
}

.trend-ylabel,
.trend-xlabel {
  font-size: 11px;
  fill: #c0c4cc;
}

.trend-hover-line {
  stroke: #dcdfe6;
  stroke-width: 1;
  stroke-dasharray: 4 3;
}

.trend-tooltip {
  position: absolute;
  top: 8px;
  transform: translateX(-50%);
  background: #fff;
  border: 1px solid #ebeef5;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 10;
}

.trend-tooltip-date {
  font-weight: 600;
  margin-bottom: 4px;
}

.trend-tooltip-row {
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.8;
}
</style>